import { IStore } from 'Store/.';
import { createSelector } from 'reselect';
import { categories } from 'Components/MenuMain/MenuMainContent';

const getAllEvents = (state: IStore) => state.events.events;
const getFilteredEvents = (state: IStore) => state.events.filteredEvents;
const getSearchedEvents = (state: IStore) => state.events.searchedEvents;
const getFilter = (state: IStore) => state.events.filter;
const getIsBoxView = (state: IStore) => state.events.isBoxView;
const getIsFetchFailed = (state: IStore) => state.events.isFetchAllFailed;
const getIsSearchResult = (state: IStore) => state.events.isSearchResult;
const getCategoryID = (state: IStore) => state.events.categoryID;
const getMaxNumberOfVisible = (state: IStore) => state.events.maxNumberOfVisible;
const getIsFetchingAllEvents = (state: IStore) => state.events.isFetchingAllEvents;
const getIsFetchingFilteredEvents = (state: IStore) => state.events.isFetchingFilteredEvents;
const getIsFetching = (state: IStore) => state.events.isFetchingAllEvents || state.events.isFetchingFilteredEvents;

const getAllEventsMergedByID = createSelector(
  [getAllEvents],
  (events) => {
    return mergeEventsByID(events);
  },
);

const getIsFiltered = createSelector(
  [getFilter], 
  (filter) => {
    const { firstDate, lastDate, city } = filter;
    return firstDate !== null || lastDate !== null || city !== null;
  },
);

const getAllOrFilteredOrSearchedEvents = createSelector(
  [getAllEvents, getFilteredEvents, getSearchedEvents, getIsFiltered, getIsSearchResult],
  (allEvents, filteredEvents, searchedEvents, isFiltered, isSearchResult) => {
    return isSearchResult ? searchedEvents : isFiltered ? filteredEvents : allEvents;
  },
);

const getEventsInThisCategory = createSelector(
  [getCategoryID, getAllOrFilteredOrSearchedEvents],
  (categoryID, events) => {
    return categoryID < 1 ? events : events.filter(e => e.categoryID === categoryID);
  },
);

const getMergedEvents = createSelector(
  [getEventsInThisCategory],
  (events) => {
    return mergeEventsByID(events);
  },
);

const getSortedEvents = createSelector(
  [getMergedEvents, getIsSearchResult],
  (events, isSearchResult) => {
    if(!isSearchResult){
      return events.sort((a, b) => (a.firstDate < b.firstDate  ? -1 : 1));
    }else{
      return events;
    }
  },
);

const getVisibleEvents = createSelector(
  [getSortedEvents, getMaxNumberOfVisible],
  (events, numberOfVisible) => {
    return events.slice(0, numberOfVisible);
  },
);

const getIsShowingAll = createSelector(
  [getMergedEvents, getVisibleEvents], 
  (events, visible) => {
    return events.length === visible.length;
  },
);

const getCities = createSelector(
  [getAllEvents], 
  (events) => {
    const cities = events.reduce((acc, e) => {
      acc[e.city] = e.city;
      return acc;
    }, {});
    return Object.keys(cities);
  },
);

const getCategory = createSelector(
  [getCategoryID],
  (categoryID) => {
    return categories.find(c => c.id === categoryID);
  },
);

const getEmptyCategoryIDs = createSelector(
  [getAllEvents],
  (events) => {

    const allIDs = categories.reduce((ids, c) => {
      ids.push(c.id);
      return ids;
    }, []);

    const countMap = {};

    events.forEach(e => {
      countMap[e.categoryID] = countMap[e.categoryID] ? 
        countMap[e.categoryID] + 1 : 1;
    });

    return allIDs.filter(id => !countMap[id]);
  },
);

const mergeEventsByID = (events: Julius.Website.EventOverviewViewModel[]) => {

  if (!events || !events.length) {
    return [];
  }

  const eventListsById = events.reduce((acc, e) => {
    acc[e.id] = acc[e.id] || [];
    acc[e.id].push(e);
    return acc;
  }, {});

  const mergedEvents: Julius.Website.EventOverviewViewModel[] = [];

  Object.keys(eventListsById).map(eventID => {
    const eventList = eventListsById[eventID];
    const mergedEvent = mergeToOneEvent(eventList);
    mergedEvents.push(mergedEvent);
  });

  return mergedEvents;
}

const mergeToOneEvent = (events: Julius.Website.EventOverviewViewModel[]) => {
  const event = events[0];

  if (events.length === 1) {
    event.isMultipleVenues = false;
    return event;
  }

  let firstDate = event.firstDate;
  let lastDate = event.lastDate;
  let isMultipleVenues = false;

  events.map((e: Julius.Website.EventOverviewViewModel) => {
    firstDate = e.firstDate.getTime() < firstDate.getTime() ? e.firstDate : firstDate;
    lastDate = e.lastDate.getTime() > lastDate.getTime() ? e.lastDate : lastDate;
    if (!isMultipleVenues) {
      isMultipleVenues = e.venue !== event.venue;
    }
  });

  const venue = isMultipleVenues ? null : event.venue;
  const city = isMultipleVenues ? null : event.city;
  const performanceID = isMultipleVenues ? null : event.performanceID;
  return { ...event, firstDate, lastDate, performanceID, venue, city, isMultipleVenues };
};

export {
  getIsFetching,
  getIsFetchingAllEvents,
  getIsFetchingFilteredEvents,
  getIsFetchFailed,
  getIsFiltered,
  getIsSearchResult,
  getIsShowingAll,
  getIsBoxView,
  getFilter,
  getCategory,
  getCities,
  getVisibleEvents,
  getEventsInThisCategory,
  getAllEventsMergedByID,
  getEmptyCategoryIDs,
};


// WEBPACK FOOTER //
// ./src/pages/EventsPage/Redux/selectors.tsx